import { useState, useEffect } from 'react';
import { fetchProjectsAPI, getQuickSummaryStatuses } from '../Dashboard/dashboardAPI';
import { fetchCMOMembersAPI, fetchDeliverableTypesAPI, fetchAllFiltersAPI, fetchMfgPlantsAPI } from '../Dashboard/filtersAPI';

const useProductDevelopment = () => {
  // Filter states
  const [cmoSite, setCmoSite] = useState('all');
  const [deliverableType, setDeliverableType] = useState('all');
  const [initialCmoSite, setInitialCmoSite] = useState('all');
  const [initialDeliverableType, setInitialDeliverableType] = useState('all');

  // CMO / Site drive the two payload dimensions:
  //   cmo  -> cmoName (display); resolves to cmoOrgId (the CMO's
  //           vendorMosymphonyID) -> payload cdmoOrgID.
  //   site -> mfgPlantShortName (display) -> canonical `cmoSite` -> payload cmoSite.
  // Site options come from fetchMfgPlants, narrowed to the selected CMO's
  // vendorMosymphonyID. Default filter is "All". (R19, R57)
  const [cmo, setCmo] = useState('all');
  const [site, setSite] = useState('all');
  // Resolved CMO org id (vendorMosymphonyID) sent as cdmoOrgID.
  const [cmoOrgId, setCmoOrgId] = useState('all');
  const [initialCmoOrgId, setInitialCmoOrgId] = useState('all');
  
  // Search states
  const [searchCriteria, setSearchCriteria] = useState('all');
  const [searchValue, setSearchValue] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Data states
  const [projects, setProjects] = useState([]);
  // Total record count from the API (totalNoOfRecords) — drives server-side pagination.
  const [totalProjects, setTotalProjects] = useState(0);
  const [quickSummaryStatuses, setQuickSummaryStatuses] = useState([]);
  // CMO filter options from fetchCMOMembers: [{ organization_id, organization_name }].
  const [cmoMembersList, setCmoMembersList] = useState([]);
  const [mfgPlantsList, setMfgPlantsList] = useState([]);
  const [deliverableTypesList, setDeliverableTypesList] = useState([]);
  
  // UI states
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [open, setOpen] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [openedProjectTab, setOpenedProjectTab] = useState({});
  const [expanded, setExpanded] = useState(false);
  const [quickSummaryFilter, setQuickSummaryFilter] = useState(null);
  
  // Modal states
  const [openActiveBatchesModal, setOpenActiveBatchesModal] = useState(false);
  const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
  const [selectedProjectForUpdate, setSelectedProjectForUpdate] = useState(null);
  
  // Action select states
  const [actionSelectValues, setActionSelectValues] = useState({});
  const [selectedBatchNumbers, setSelectedBatchNumbers] = useState({});
  const [selectedUnitProcedures, setSelectedUnitProcedures] = useState({});
  
  // NEW: Dropdown selection states per project (for cascading dropdowns)
  // Structure: { projectId: value }
  const [selectedCampaigns, setSelectedCampaigns] = useState({}); // { projectId: campaignId }
  const [selectedStages, setSelectedStages] = useState({}); // { projectId: stageId }
  const [selectedBatches, setSelectedBatches] = useState({}); // { projectId: { batchNumber, batchId } }
  const [selectedOperations, setSelectedOperations] = useState({}); // { projectId: operation }
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Save / Clear filters result dialog. These two actions confirm in a popup
  // (like the Purchase Order dashboard) rather than the top-centre snackbar.
  const [filterDialog, setFilterDialog] = useState({
    open: false,
    success: true,
    title: '',
  });

  // Last sync time
  const [lastSyncTime, setLastSyncTime] = useState(new Date());

  // Track if initial load is complete to prevent API call on mount
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Load dashboard data
  const loadDashboardData = async (filters = {}) => {
    try {
      setLoading(true);
      setApiError(null);
      
      const filtersToUse = {
        cmoSite: filters.cmoSite || cmoSite,
        cmoOrgId: filters.cmoOrgId || cmoOrgId,
        deliverableType: filters.deliverableType || deliverableType,
        projectStatusID: 'projectStatusID' in filters ? filters.projectStatusID : quickSummaryFilter,
        searchCriteria: filters.searchCriteria || searchCriteria,
        searchValue: filters.searchValue || searchValue,
        pageNumber: filters.pageNumber || currentPage,
        pageSize: filters.pageSize || rowsPerPage,
      };

      // Fetch projects and quick summary in parallel
      const [projectsResponse, quickSummaryResponse] = await Promise.all([
        fetchProjectsAPI(filtersToUse, cmoMembersList, deliverableTypesList),
        getQuickSummaryStatuses({
          cmoSite: filtersToUse.cmoSite,
          cmoOrgId: filtersToUse.cmoOrgId,
          deliverableType: filtersToUse.deliverableType,
        }, cmoMembersList, deliverableTypesList),
      ]);

      // Check if API call failed
      if (!projectsResponse?.status) {
        setApiError('Failed to fetch projects. Please try again.');
        setProjects([]);
        setTotalProjects(0);
      } else if (projectsResponse?.projects) {
        setProjects(projectsResponse.projects);
        // Server-side pagination: `projects` is only the current page, so drive
        // the pager off the API's total record count (totalNoOfRecords).
        setTotalProjects(
          typeof projectsResponse.totalProjects === 'number'
            ? projectsResponse.totalProjects
            : projectsResponse.projects.length
        );
        // Clear error if data is successfully loaded
        if (projectsResponse.projects.length === 0) {
          setApiError(null); // No error, just no data
        } else {
          setApiError(null);
        }
      } else {
        setProjects([]);
        setTotalProjects(0);
        setApiError(null);
      }

      if (quickSummaryResponse?.quickSummaryStatuses) {
        setQuickSummaryStatuses(quickSummaryResponse.quickSummaryStatuses);
      }

      setLastSyncTime(new Date());
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setApiError('Failed to load dashboard data. Please try again.');
      setProjects([]);
      setTotalProjects(0);
      setSnackbar({
        open: true,
        message: 'Failed to load dashboard data',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize component
  useEffect(() => {
    const initialize = async () => {
      try {
        setFiltersLoading(true);

        // Fetch dropdown options. CMO filter options come from fetchCMOMembers
        // (same source as the Purchase Order Dashboard); Site options from
        // fetchMfgPlants.
        const [cmoMembersResponse, mfgPlantsResponse, deliverableTypesResponse, savedFilters] = await Promise.all([
          fetchCMOMembersAPI(),
          fetchMfgPlantsAPI(),
          fetchDeliverableTypesAPI(),
          fetchAllFiltersAPI(),
        ]);

        // Site filter options come from fetchMfgPlants (mfgPlantShortName).
        const mfgPlantsData = Array.isArray(mfgPlantsResponse?.data) ? mfgPlantsResponse.data : [];
        setMfgPlantsList(mfgPlantsData);

        // CMO members: [{ organization_id, organization_name }].
        const cmoMembersData = Array.isArray(cmoMembersResponse?.data) ? cmoMembersResponse.data : [];
        setCmoMembersList(cmoMembersData);

        if (deliverableTypesResponse?.data || deliverableTypesResponse?.displayData) {
          // Store full objects for ID mapping, use displayData if data is array of objects
          const fullData = Array.isArray(deliverableTypesResponse.data) && deliverableTypesResponse.data.length > 0 && typeof deliverableTypesResponse.data[0] === 'object'
            ? deliverableTypesResponse.data
            : (deliverableTypesResponse.displayData || deliverableTypesResponse.data || []);
          setDeliverableTypesList(fullData);
        }

        // Apply saved filters if available
        if (savedFilters?.status && savedFilters?.save_filter_options) {
          const saved = savedFilters.save_filter_options;
          // savedCmoSite is the mfgPlantShortName (Site name); savedCmoOrgId is
          // the CMO's vendorMosymphonyID. Both default to "all".
          const savedCmoSite = saved.cmoSite || 'all';
          const savedCmoOrgId = saved.cmoOrgId || 'all';
          const savedDeliverableType = saved.deliverableType || 'all';

          setCmoSite(savedCmoSite);
          setCmoOrgId(savedCmoOrgId);
          setDeliverableType(savedDeliverableType);
          setInitialCmoSite(savedCmoSite);
          setInitialCmoOrgId(savedCmoOrgId);
          setInitialDeliverableType(savedDeliverableType);

          // Restore the dropdown display: `cmo` holds the CMO's organization_id
          // (the saved cmoOrgId); `site` holds the mfgPlantShortName.
          if (savedCmoOrgId && savedCmoOrgId !== 'all') {
            setCmo(String(savedCmoOrgId));
          }
          if (savedCmoSite && savedCmoSite !== 'all') {
            setSite(savedCmoSite);
          }

          // Load data with saved filters
          await loadDashboardData({
            cmoSite: savedCmoSite,
            cmoOrgId: savedCmoOrgId,
            deliverableType: savedDeliverableType,
          });
        } else {
          // Load data with default filters
          await loadDashboardData();
        }
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        setSnackbar({
          open: true,
          message: 'Failed to initialize dashboard',
          severity: 'error',
        });
      } finally {
        setFiltersLoading(false);
        setInitialLoadComplete(true);
      }
    };

    initialize();
  }, []);

  // Auto-call API when filters change (after initial load)
  useEffect(() => {
    // Skip if initial load not complete (prevents duplicate call on mount)
    if (!initialLoadComplete) {
      return;
    }

    // Clear quick summary filter when main filters change
    setQuickSummaryFilter(null);
    setCurrentPage(1);

    // Call API with updated filters
    loadDashboardData({
      cmoSite,
      cmoOrgId,
      deliverableType,
      projectStatusID: null, // Clear quick summary filter
      searchCriteria,
      searchValue,
      pageNumber: 1,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmoSite, cmoOrgId, deliverableType]);

  // Auto-call API when pagination changes (after initial load)
  useEffect(() => {
    // Skip if initial load not complete (prevents duplicate call on mount)
    if (!initialLoadComplete) {
      return;
    }

    // Call API with updated pagination
    loadDashboardData({
      cmoSite,
      cmoOrgId,
      deliverableType,
      projectStatusID: quickSummaryFilter,
      searchCriteria,
      searchValue,
      pageNumber: currentPage,
      pageSize: rowsPerPage,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, rowsPerPage]);

  return {
    // State
    cmoSite,
    setCmoSite,
    cmo,
    setCmo,
    site,
    setSite,
    cmoOrgId,
    setCmoOrgId,
    initialCmoOrgId,
    deliverableType,
    setDeliverableType,
    searchCriteria,
    setSearchCriteria,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    initialCmoSite,
    initialDeliverableType,
    rowsPerPage,
    setRowsPerPage,
    filtersLoading,
    snackbar,
    setSnackbar,
    filterDialog,
    setFilterDialog,
    projects,
    totalProjects,
    quickSummaryStatuses,
    apiError,
    open,
    setOpen,
    selectedProject,
    setSelectedProject,
    openedProjectTab,
    setOpenedProjectTab,
    lastSyncTime,
    quickSummaryFilter,
    setQuickSummaryFilter,
    expanded,
    setExpanded,
    openActiveBatchesModal,
    setOpenActiveBatchesModal,
    updateStatusModalOpen,
    setUpdateStatusModalOpen,
    selectedProjectForUpdate,
    setSelectedProjectForUpdate,
    actionSelectValues,
    setActionSelectValues,
    selectedBatchNumbers,
    setSelectedBatchNumbers,
    selectedUnitProcedures,
    setSelectedUnitProcedures,
    selectedCampaigns,
    setSelectedCampaigns,
    selectedStages,
    setSelectedStages,
    selectedBatches,
    setSelectedBatches,
    selectedOperations,
    setSelectedOperations,
    cmoMembersList,
    mfgPlantsList,
    deliverableTypesList,
    loading,
    // Functions
    loadDashboardData,
    setInitialCmoSite,
    setInitialCmoOrgId,
    setInitialDeliverableType,
    setQuickSummaryStatuses,
    setProjects,
    setLastSyncTime,
    setLoading,
    setFiltersLoading,
  };
};

export { useProductDevelopment };
export default useProductDevelopment;

